import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const fetchOrders = createAsyncThunk(
    'order/fetchOrders',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                apiUrls.ordersOrderListing,
                params,
                "GET",
                {}
            );

            if (response?.data?.success) {
                const ordersData = response.data.data.data;
                const paginationData = response.data.data;

                const transformedOrders = ordersData.map(order => {
                    const firstItem = order.items?.[0] || {};
                    let size = "";
                    let thickness = ""
                    let voltage = ""
                    let productImage = "/image/placeholder.png";
                    if (firstItem.battery_detail_id != null && firstItem.product?.battery_variants?.[0]) {
                        productImage = firstItem.product.battery_variants[0].images?.[0]?.image_url || productImage;
                        voltage = firstItem.product.battery_variants[0].voltage || size;
                    }
                    if (firstItem.tire_detail_id != null && firstItem.product?.tyre_variants?.[0]) {
                        productImage = firstItem.product.tyre_variants[0].images?.[0]?.image_url || productImage;
                        size = firstItem.product.tyre_variants[0].size?.name || size;
                    }
                    if (firstItem.windshield_detail_id != null && firstItem.product?.windshield_variants?.[0]) {
                        productImage = firstItem.product.windshield_variants[0].images?.[0]?.image_url || productImage;
                        thickness = firstItem.product.windshield_variants[0].thickness || size;
                    }

                    return {
                        id: order.id,
                        orderNumber: order.order_number,
                        item: {
                            name: firstItem.product_name || "Order Items",
                            image: productImage,
                            quantity: firstItem.quantity || 1,
                            size: size,
                            voltage: voltage,
                            thickness: thickness,
                            items: order.items
                        },
                        amount: parseFloat(order.total_amount),
                        orderDate: order.created_at?.split('T')[0] || '',
                        deliveryDate: order.updated_at?.split('T')[0] || '',
                        status: order.status,
                        canCancel: ['pending', 'confirmed'].includes(order.status),
                        canTrack: true,
                        trackingNumber: order.transaction_id || null,
                        shippingAddress: order.shipping_address,
                        billingAddress: order.billing_address,
                        paymentStatus: order.payment_status,
                        paymentMethod: order.payment_method,
                        transactionId: order.transaction_id,
                        user: order.user,
                        payments: order.payments,
                        items: order.items
                    };
                });

                return {
                    orders: transformedOrders,
                    pagination: {
                        currentPage: paginationData.current_page,
                        lastPage: paginationData.last_page,
                        perPage: paginationData.per_page,
                        total: paginationData.total,
                        links: paginationData.links || []
                    }
                };
            }
            return { orders: [], pagination: null };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch orders');
        }
    }
);

export const fetchOrderById = createAsyncThunk(
    'order/fetchOrderById',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.orderDetail}/${orderId}`,
                {},
                "GET",
                {}
            );
            if (response?.data?.success && response?.data?.data) {
                const order = response.data.data;

                const transformedItems = order.items.map(item => {
                    let variantImage = "/image/placeholder.png";
                    let variantDetails = {};

                    if (item.battery_detail_id && item.product?.battery_variants?.length > 0) {
                        const variant = item.product.battery_variants.find(v => v.id === item.battery_detail_id);
                        if (variant) {
                            variantImage = variant.images?.[0]?.image_url || "/image/placeholder.png";
                            variantDetails = {
                                capacity: variant.capacity,
                                voltage: variant.voltage,
                                warranty: variant.warranty,
                                type: 'battery'
                            };
                        }
                    }

                    if (item.tire_detail_id && item.product?.tyre_variants?.length > 0) {
                        const variant = item.product.tyre_variants.find(v => v.id === item.tire_detail_id);
                        if (variant) {
                            variantImage = variant.images?.[0]?.image_url || "/image/placeholder.png";
                            variantDetails = {
                                size: variant.size?.name,
                                speed_rating: variant.speed_rating,
                                load_index: variant.load_index,
                                type: 'tire'
                            };
                        }
                    }

                    if (item.windshield_detail_id && item.product?.windshield_variants?.length > 0) {
                        const variant = item.product.windshield_variants.find(v => v.id === item.windshield_detail_id);
                        if (variant) {
                            variantImage = variant.images?.[0]?.image_url || "/image/placeholder.png";
                            variantDetails = {
                                thickness: variant.thickness,
                                material: variant.material,
                                type: 'windshield'
                            };
                        }
                    }

                    return {
                        ...item,
                        variant_image: variantImage,
                        variant_details: variantDetails
                    };
                });

                const transformedOrder = {
                    id: order.id,
                    orderNumber: order.order_number,
                    subtotal: parseFloat(order.subtotal),
                    tax: parseFloat(order.tax),
                    shipping_charge: parseFloat(order.shipping_charge),
                    total_amount: parseFloat(order.total_amount),
                    status: order.status,
                    payment_status: order.payment_status,
                    payment_method: order.payment_method,
                    transaction_id: order.transaction_id,
                    created_at: order.created_at,
                    updated_at: order.updated_at,
                    items: transformedItems,
                    payments: order.payments || [],
                    user: order.user,
                    billing_address: order.billing_address,
                    shipping_address: order.shipping_address,
                    canCancel: ['pending', 'confirmed'].includes(order.status),
                    canTrack: order.status === 'processing' || order.status === 'shipped' || order.status === 'confirmed'
                };

                return transformedOrder;
            }
            return rejectWithValue('Order not found');
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch order details');
        }
    }
);

export const cancelOrder = createAsyncThunk(
    'order/cancelOrder',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.cancelOrder}/${orderId}`,
                {},
                "POST",
                {}
            );

            if (response?.success) {
                return {
                    orderId,
                    status: 'cancelled',
                    canCancel: false,
                    message: response.message
                };
            }
            return rejectWithValue(response?.message || 'Failed to cancel order');
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to cancel order');
        }
    }
);

export const trackOrder = createAsyncThunk(
    'order/trackOrder',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.trackOrder}/${orderId}`,
                {},
                "GET",
                {}
            );

            if (response?.success && response?.data) {
                return {
                    orderId: orderId,
                    orderNumber: response.data.order_number,
                    trackingNumber: response.data.tracking_number || `TRK${orderId}`,
                    status: response.data.status,
                    estimatedDelivery: response.data.estimated_delivery,
                    currentLocation: response.data.current_location || "Processing",
                    carrier: response.data.carrier || "Standard Shipping",
                    timeline: response.data.timeline || [
                        { date: response.data.created_at?.split('T')[0], status: "Order Placed", location: "Online" }
                    ]
                };
            }
            return rejectWithValue(response?.message || 'Failed to track order');
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to track order');
        }
    }
);

export const reorderItems = createAsyncThunk(
    'order/reorderItems',
    async (orderId, { rejectWithValue }) => {
        try {
            let orderData = orderId;
            const response = await callAPI(
                `${apiUrls.orderDetail}/${orderId}`,
                {},
                "GET",
                {}
            );

            if (response?.data?.success) {
                orderData = response.data.data;
            } else {
                throw new Error('Order not found');
            }
            const promises = orderData.items.map(async (item) => {
                const formData = new FormData();

                formData.append('product_id', item.product_id);
                formData.append('quantity', item.quantity);
                if (item.battery_detail_id) {
                    formData.append('battery_detail_id', item.battery_detail_id);
                }
                if (item.tire_detail_id) {
                    formData.append('tire_detail_id', item.tire_detail_id);
                }
                if (item.windshield_detail_id) {
                    formData.append('windshield_detail_id', item.windshield_detail_id);
                }

                const response = await callAPI(
                    apiUrls.addToCart,
                    {},
                    "POST",
                    formData,
                    true
                );

                return response.data;
            });

            const results = await Promise.all(promises);

            return {
                success: true,
                message: 'Items added to cart successfully',
                items: results
            };

        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to reorder items');
        }
    }
);

export const downloadInvoice = createAsyncThunk(
    'order/downloadInvoice',
    async ({ orderId, orderNumber }, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.orderInvoice}/${orderId}`,
                {},
                "GET",
                null,
                {
                    responseType: "blob",
                },
                {
                    Accept: "application/pdf"
                }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `invoice_${orderNumber}.pdf`;
            link.click();
            link.remove();
            return true;

        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to download invoice'
            );
        }
    }
);

const applyFiltersToOrders = (orders, searchTerm, statusFilter, dateRange) => {
    let result = [...orders];

    if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        result = result.filter(order =>
            order.orderNumber.toLowerCase().includes(searchLower) ||
            (order.item?.name || '').toLowerCase().includes(searchLower)
        );
    }

    if (statusFilter !== "all") {
        result = result.filter(order => order.status === statusFilter);
    }

    if (dateRange !== "all") {
        const now = new Date();
        switch (dateRange) {
            case "last30":
                result = result.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    const diffTime = now - orderDate;
                    const diffDays = diffTime / (1000 * 60 * 60 * 24);
                    return diffDays <= 30;
                });
                break;
            case "last90":
                result = result.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    const diffTime = now - orderDate;
                    const diffDays = diffTime / (1000 * 60 * 60 * 24);
                    return diffDays <= 90;
                });
                break;
            default:
                break;
        }
    }

    return result;
};

const orderSlice = createSlice({
    name: 'order',
    initialState: {
        orders: [],
        filteredOrders: [],
        currentOrder: null,
        trackingInfo: null,
        pagination: {
            currentPage: 1,
            lastPage: 1,
            perPage: 10,
            total: 0,
            links: []
        },

        searchTerm: "",
        statusFilter: "",
        dateRange: "",

        dropdownOpen: null,

        loading: false,
        ordersLoading: false,
        orderDetailsLoading: false,
        cancelLoading: false,
        trackLoading: false,
        reorderLoading: false,
        invoiceLoading: false,

        message: {
            type: "",
            text: ""
        },

        error: null,

        cancelSuccess: false,
        reorderSuccess: false,
        invoiceSuccess: false
    },
    reducers: {
        setSearchTerm: (state, action) => {
            state.searchTerm = action.payload;
        },

        setStatusFilter: (state, action) => {
            state.statusFilter = action.payload;
        },

        setTimeFilter: (state, action) => {
            state.timeFilter = action.payload;
        },
        resetFilters: (state) => {
            state.searchTerm = "";
            state.statusFilter = "";
            state.timeFilter = "";
        },

        setDropdownOpen: (state, action) => {
            state.dropdownOpen =
                state.dropdownOpen === action.payload ? null : action.payload;
        },

        closeDropdown: (state) => {
            state.dropdownOpen = null;
        },

        setMessage: (state, action) => {
            state.message = {
                type: action.payload.type,
                text: action.payload.text
            };
        },

        clearMessage: (state) => {
            state.message = {
                type: "",
                text: ""
            };
            state.error = null;
        },

        clearOrderError: (state) => {
            state.error = null;
        },

        clearCurrentOrder: (state) => {
            state.currentOrder = null;
        },

        clearTrackingInfo: (state) => {
            state.trackingInfo = null;
        },

        clearCancelSuccess: (state) => {
            state.cancelSuccess = false;
        },

        clearReorderSuccess: (state) => {
            state.reorderSuccess = false;
        },

        clearInvoiceSuccess: (state) => {
            state.invoiceSuccess = false;
        },

        resetOrderState: (state) => {
            state.orders = [];
            state.filteredOrders = [];
            state.currentOrder = null;
            state.trackingInfo = null;
            state.searchTerm = "";
            state.statusFilter = "all";
            state.dateRange = "all";
            state.dropdownOpen = null;
            state.message = { type: "", text: "" };
            state.error = null;
            state.cancelSuccess = false;
            state.reorderSuccess = false;
            state.invoiceSuccess = false;
        },

        setPage: (state, action) => {
            state.pagination.currentPage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrders.pending, (state) => {
                state.ordersLoading = true;
                state.error = null;
            })
            .addCase(fetchOrders.fulfilled, (state, action) => {
                state.ordersLoading = false;
                state.orders = action.payload.orders;
                state.pagination = action.payload.pagination || state.pagination;
            })
            .addCase(fetchOrders.rejected, (state, action) => {
                state.ordersLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchOrderById.pending, (state) => {
                state.loading = true;
                state.orderDetailsLoading = true;
                state.error = null;
            })
            .addCase(fetchOrderById.fulfilled, (state, action) => {
                state.loading = false;
                state.orderDetailsLoading = false;
                state.currentOrder = action.payload;
            })
            .addCase(fetchOrderById.rejected, (state, action) => {
                state.loading = false;
                state.orderDetailsLoading = false;
                state.error = action.payload || 'Failed to fetch order details';
                state.message = {
                    type: 'error',
                    text: action.payload || 'Failed to fetch order details'
                };
            })

            .addCase(cancelOrder.pending, (state) => {
                state.cancelLoading = true;
                state.cancelSuccess = false;
                state.error = null;
            })
            .addCase(cancelOrder.fulfilled, (state, action) => {
                state.cancelLoading = false;
                state.cancelSuccess = true;
                state.dropdownOpen = null;

                const orderIndex = state.orders.findIndex(o => o.id === action.payload.orderId);
                if (orderIndex !== -1) {
                    state.orders[orderIndex].status = action.payload.status;
                    state.orders[orderIndex].canCancel = action.payload.canCancel;
                }

                const filteredIndex = state.filteredOrders.findIndex(o => o.id === action.payload.orderId);
                if (filteredIndex !== -1) {
                    state.filteredOrders[filteredIndex].status = action.payload.status;
                    state.filteredOrders[filteredIndex].canCancel = action.payload.canCancel;
                }

                if (state.currentOrder && state.currentOrder.id === action.payload.orderId) {
                    state.currentOrder.status = action.payload.status;
                    state.currentOrder.canCancel = action.payload.canCancel;
                }

                state.message = {
                    type: 'success',
                    text: action.payload.message || 'Order cancelled successfully'
                };
            })
            .addCase(cancelOrder.rejected, (state, action) => {
                state.cancelLoading = false;
                state.cancelSuccess = false;
                state.dropdownOpen = null;
                state.error = action.payload || 'Failed to cancel order';
                state.message = {
                    type: 'error',
                    text: action.payload || 'Failed to cancel order'
                };
            })

            .addCase(trackOrder.pending, (state) => {
                state.trackLoading = true;
                state.error = null;
            })
            .addCase(trackOrder.fulfilled, (state, action) => {
                state.trackLoading = false;
                state.trackingInfo = action.payload;
                state.dropdownOpen = null;
                state.message = {
                    type: 'success',
                    text: 'Tracking information retrieved successfully'
                };
            })
            .addCase(trackOrder.rejected, (state, action) => {
                state.trackLoading = false;
                state.dropdownOpen = null;
                state.error = action.payload || 'Failed to track order';
                state.message = {
                    type: 'error',
                    text: action.payload || 'Failed to track order'
                };
            })

            .addCase(reorderItems.pending, (state) => {
                state.reorderLoading = true;
                state.reorderSuccess = false;
                state.error = null;
            })
            .addCase(reorderItems.fulfilled, (state, action) => {
                state.reorderLoading = false;
                state.reorderSuccess = true;
                state.dropdownOpen = null;
                state.message = {
                    type: 'success',
                    text: action.payload.message || 'Items added to cart successfully'
                };
            })
            .addCase(reorderItems.rejected, (state, action) => {
                state.reorderLoading = false;
                state.reorderSuccess = false;
                state.dropdownOpen = null;
                state.error = action.payload || 'Failed to reorder items';
                state.message = {
                    type: 'error',
                    text: action.payload || 'Failed to reorder items'
                };
            })

            .addCase(downloadInvoice.pending, (state) => {
                state.invoiceLoading = true;
                state.invoiceSuccess = false;
                state.error = null;
            })
            .addCase(downloadInvoice.fulfilled, (state, action) => {
                state.invoiceLoading = false;
                state.invoiceSuccess = true;
                state.dropdownOpen = null;
                state.message = {
                    type: 'success',
                    text: 'Invoice downloaded successfully'
                };
            })
            .addCase(downloadInvoice.rejected, (state, action) => {
                state.invoiceLoading = false;
                state.invoiceSuccess = false;
                state.dropdownOpen = null;
                state.error = action.payload || 'Failed to download invoice';
                state.message = {
                    type: 'error',
                    text: action.payload || 'Failed to download invoice'
                };
            });
    }
});

export const {
    setSearchTerm,
    setStatusFilter,
    setTimeFilter,
    resetFilters,
    applyFilters,
    setDropdownOpen,
    closeDropdown,
    setMessage,
    clearMessage,
    clearOrderError,
    clearCurrentOrder,
    clearTrackingInfo,
    clearCancelSuccess,
    clearReorderSuccess,
    clearInvoiceSuccess,
    resetOrderState,
    setPage
} = orderSlice.actions;

export default orderSlice.reducer;